import api from './api';
import {
  hydrateDisputeCollection,
  hydrateDisputeRecord
} from '../utils/translationHydrator';

const hydrateDisputePayload = (payload) => {
  if (!payload) {
    return payload;
  }

  if (Array.isArray(payload.data)) {
    return { ...payload, data: hydrateDisputeCollection(payload.data) };
  }

  if (payload.data) {
    return { ...payload, data: hydrateDisputeRecord(payload.data) };
  }

  return payload;
};

class DisputeAdminService {
  async list(params = {}) {
    const response = await api.get('/disputes', { params });
    return hydrateDisputePayload(response.data);
  }

  async get(disputeId) {
    const response = await api.get(`/disputes/${disputeId}`);
    return hydrateDisputePayload(response.data);
  }

  async create(payload) {
    // If payload is FormData, send directly
    if (payload instanceof FormData) {
      const response = await api.post('/disputes', payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return hydrateDisputePayload(response.data);
    }
    
    const response = await api.post('/disputes', payload);
    return hydrateDisputePayload(response.data);
  }

  async addEvent(disputeId, payload) {
    // If payload is FormData, send directly
    if (payload instanceof FormData) {
      const response = await api.post(`/disputes/${disputeId}/events`, payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return hydrateDisputePayload(response.data);
    }
    const response = await api.post(`/disputes/${disputeId}/events`, payload);
    return hydrateDisputePayload(response.data);
  }

  async addAttachment(disputeId, payload) {
    const response = await api.post(`/disputes/${disputeId}/attachments`, payload);
    return hydrateDisputePayload(response.data);
  }

  async updateStatus(disputeId, payload) {
    const response = await api.patch(`/disputes/${disputeId}/status`, payload);
    return hydrateDisputePayload(response.data);
  }

  async proposeResolution(disputeId, payload) {
    const response = await api.post(`/disputes/${disputeId}/propose-resolution`, payload);
    return hydrateDisputePayload(response.data);
  }

  async acceptResolution(disputeId) {
    const response = await api.post(`/disputes/${disputeId}/accept-resolution`);
    return hydrateDisputePayload(response.data);
  }

  /**
   * Confirmer l'authentification 3DS après validation du deposit
   * @param {string} disputeId 
   * @param {string} paymentIntentId - ID du PaymentIntent après 3DS
   */
  async confirmDepositAuthentication(disputeId, paymentIntentId) {
    const response = await api.post(`/disputes/${disputeId}/confirm-deposit-authentication`, {
      payment_intent_id: paymentIntentId
    });
    return hydrateDisputePayload(response.data);
  }

  /**
   * Annuler l'authentification 3DS en cours (abandon, échec, fermeture)
   * @param {string} disputeId 
   * @param {string} paymentIntentId - ID du PaymentIntent
   * @param {string} reason - Raison: 'authentication_failed' | 'authentication_abandoned' | 'modal_closed'
   */
  async cancelDepositAuthentication(disputeId, paymentIntentId, reason = 'authentication_abandoned') {
    const response = await api.post(`/disputes/${disputeId}/cancel-deposit-authentication`, {
      payment_intent_id: paymentIntentId,
      reason
    });
    return hydrateDisputePayload(response.data);
  }

  async rejectResolution(disputeId, reason = null) {
    const response = await api.post(`/disputes/${disputeId}/reject-resolution`, { reason });
    return hydrateDisputePayload(response.data);
  }

  async counterProposal(disputeId, payload) {
    const response = await api.post(`/disputes/${disputeId}/counter-proposal`, payload);
    return hydrateDisputePayload(response.data);
  }

  async escalate(disputeId, payload) {
    const response = await api.post(`/disputes/${disputeId}/escalate`, payload);
    return hydrateDisputePayload(response.data);
  }

  /**
   * Décision administrative sur un litige escaladé (admin seulement)
   * @param {string} disputeId 
   * @param {Object} payload - { decision, message, amount, ownerAmount, renterAmount }
    * decision: 'favor_owner' | 'favor_renter' | 'shared' | 'dismiss' | 'return_to_parties' | 'cancel_reservation'
   */
  async adminDecision(disputeId, payload) {
    const response = await api.post(`/disputes/${disputeId}/admin-decision`, payload);
    return hydrateDisputePayload(response.data);
  }

  /**
   * Envoyer un message privé à une partie (admin ou partie concernée)
   * @param {string} disputeId 
   * @param {Object|FormData} payload - { message, visibility } ou FormData
   * visibility: 'owner_only' | 'renter_only'
   */
  async sendPrivateMessage(disputeId, payload) {
    // If payload is FormData, send with multipart header
    if (payload instanceof FormData) {
      const response = await api.post(`/disputes/${disputeId}/private-message`, payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return hydrateDisputePayload(response.data);
    }
    const response = await api.post(`/disputes/${disputeId}/private-message`, payload);
    return hydrateDisputePayload(response.data);
  }

  /**
   * Récupérer les événements filtrés par visibilité selon le rôle
   * @param {string} disputeId 
   * @param {string} visibility - optionnel: 'public' | 'owner_only' | 'renter_only' | 'admin_only' | 'all'
   */
  async getFilteredEvents(disputeId, visibility = null) {
    const params = visibility ? { visibility } : {};
    const response = await api.get(`/disputes/${disputeId}/events`, { params });
    return response.data;
  }
  
  /**
   * Récupérer les pénalités appliquées pour un dispute
   * @param {string} disputeId 
   */
  async getPenalties(disputeId) {
    const response = await api.get(`/penalties`, {
      params: {
        source_type: 'dispute',
        source_id: disputeId
      }
    });
    return response.data;
  }

  /**
   * Récupérer le statut de ban des parties d'un dispute
   * @param {string} disputeId 
   */
  async getPartiesBanStatus(disputeId) {
    const response = await api.get(`/disputes/${disputeId}/parties-ban-status`);
    return response.data;
  }
}

export default new DisputeAdminService();
